import {
    Autopilot,
    AutopilotRunType,
    autopilotPlanStarted,
    BriefStatus,
    ExecutionId,
    IChatMessageExecutionIntentMetaData,
    IChatMessageThreadData,
    StartAutopilotJobRequestBody,
} from "@buildwithflux/models";
import {ChatRepository} from "@buildwithflux/repositories";
import {Logger} from "@buildwithflux/shared";
import axios, {type AxiosInstance, type AxiosResponse} from "axios";

import {UseCopilotChatStore} from "components/pages/document/components/editor/components/right_drawer/copilot_chat/state/useCopilotChatStore";
import {CurrentUserService} from "modules/auth";
import {FluxAuthClaimsService} from "modules/auth/services/FluxAuthClaimsService";
import {UseAuthPermissionsStore} from "modules/auth/state/permissions";

/**
 * Response from the syncAutopilotState watchdog endpoint
 */
export interface SyncAutopilotStateResponse {
    /** Whether the state was modified (orphaned stopRequested was cleared) */
    stateCleared: boolean;
    /** Current brief_status after sync */
    briefStatus: BriefStatus | undefined;
    /** Current stopRequested value after sync */
    stopRequested: boolean;
    /** Current executionUpdatedAt timestamp after sync */
    executionUpdatedAt: number | undefined;
}

export interface AutopilotService {
    /**
     * Starts a new auto pilot job
     */
    startJob(
        documentUid: string,
        currentThreadUid: string,
        latestThreadBrief: NonNullable<IChatMessageThreadData["latest_brief"]>,
        promptVersion?: string,
        runType?: AutopilotRunType,
    ): Promise<void>;

    /**
     * Watchdog endpoint to detect and clean up orphaned stopRequested flags.
     * If stopRequested is true but no autopilot job is running (brief_status is not "working"),
     * this endpoint clears the stopRequested flag to resolve the state mismatch.
     */
    syncAutopilotState(documentUid: string, threadUid: string): Promise<SyncAutopilotStateResponse | undefined>;
}

export class HttpAutopilotService implements AutopilotService {
    private readonly axiosClient: AxiosInstance;

    constructor(
        private readonly currentUserService: CurrentUserService,
        private readonly useCopilotChatStore: UseCopilotChatStore,
        private readonly chatRepository: ChatRepository,
        private readonly fluxAuthClaimsService: FluxAuthClaimsService,
        private readonly logger: Logger,
        private readonly useAuthPermissionsStore: UseAuthPermissionsStore,
    ) {
        this.axiosClient = this.createAxiosClient();
    }

    public async startJob(
        documentUid: string,
        currentThreadUid: string,
        latestThreadBrief: NonNullable<IChatMessageThreadData["latest_brief"]>,
        promptVersion?: string,
        runType?: AutopilotRunType,
    ): Promise<void> {
        // Check edit permissions before proceeding
        const hasEditPermission = this.useAuthPermissionsStore.getState().currentUserHasEditPermission;
        if (!hasEditPermission) {
            this.logger.error("AutoPilot startJob - User does not have edit permission");
            return undefined;
        }

        const metaData: IChatMessageExecutionIntentMetaData = {
            type: "executionIntent",
            contentSummary: autopilotPlanStarted,
            prompt_version: promptVersion,
            runType,
        };
        const newMessageUid = await this.useCopilotChatStore
            .getState()
            .createMessage(currentThreadUid, latestThreadBrief?.content, metaData);

        const currentUserUid = this.currentUserService.getCurrentUser()?.uid;

        if (!newMessageUid) {
            this.logger.error(`AutoPilot startJob - Error when creating executionIntent message`);
            return undefined;
        }
        if (!currentUserUid) {
            this.logger.error(`AutoPilot startJob - Missing currentUser`);
            return undefined;
        }

        // Use updateThread to only update specific latest_brief fields without overwriting
        // backend-written fields like executionUpdatedAt.
        // Set executionId to "booting" to indicate a job is starting but hasn't received an ID yet.
        // This prevents the watchdog from trying to resolve an old executionId during boot.
        await this.chatRepository.updateThread(documentUid, currentThreadUid, {
            latest_brief: {
                brief_status: "working",
                stopRequested: false, // Clear any previous stop request
                executionId: ExecutionId.booting(), // Mark as booting until backend assigns real ID
            },
        });

        const url = this.getStartUrl();

        const requestBody = StartAutopilotJobRequestBody.parse({
            projectUid: documentUid,
            threadUid: currentThreadUid,
            messageUid: newMessageUid,
            userUid: currentUserUid,
        });

        let response;
        try {
            response = await this.postWithRetry<{success: boolean; message: string; executionId?: string}>(
                url,
                requestBody,
            );
        } catch (error) {
            this.logger.error("AutoPilot startJob - All retries exhausted for /start POST", error);
            await this.resetThreadBrief(documentUid, currentThreadUid);
            return;
        }

        try {
            if (response.data.executionId) {
                await this.chatRepository.setMessage(documentUid, currentThreadUid, {
                    uid: newMessageUid,
                    meta_data: {
                        ...metaData,
                        executionId: response.data.executionId,
                    },
                });
            }
        } catch (error) {
            this.logger.error("AutoPilot startJob - Failed to update message with executionId", error);
        }

        this.logger.info("Started autopilot job");
    }

    public async syncAutopilotState(
        documentUid: string,
        threadUid: string,
    ): Promise<SyncAutopilotStateResponse | undefined> {
        const currentUserUid = this.currentUserService.getCurrentUser()?.uid;

        if (!currentUserUid) {
            this.logger.error("syncAutopilotState - Missing currentUser");
            return undefined;
        }

        const url = this.getSyncUrl();

        try {
            const response = await this.postWithRetry<SyncAutopilotStateResponse>(url, {
                projectUid: documentUid,
                threadUid,
                userUid: currentUserUid,
            });

            if (response.data.stateCleared) {
                this.logger.info("syncAutopilotState - Cleared orphaned stopRequested flag");
            }

            return response.data;
        } catch (error) {
            this.logger.error("syncAutopilotState - All retries exhausted", error);
            return undefined;
        }
    }

    /**
     * Gets the URL for starting an autopilot job.
     * Uses Autopilot.resolve() to route through backend-proxy in PR preview environments.
     */
    private getStartUrl(): string {
        return Autopilot.resolve("/start");
    }

    /**
     * Gets the URL for syncing autopilot state.
     * Uses Autopilot.resolve() to route through backend-proxy in PR preview environments.
     */
    private getSyncUrl(): string {
        return Autopilot.resolve("/sync");
    }

    private async resetThreadBrief(documentUid: string, threadUid: string): Promise<void> {
        try {
            await this.chatRepository.updateThread(documentUid, threadUid, {
                latest_brief: {
                    brief_status: "failed",
                    stopRequested: false,
                    executionId: ExecutionId.none(),
                },
            });
        } catch (resetError) {
            this.logger.error("AutoPilot startJob - Failed to reset thread brief after error", resetError);
        }
    }

    private isRetryableError(error: import("axios").AxiosError): boolean {
        if (!error.response) {
            return true;
        }
        const status = error.response.status;
        return status >= 500 && status < 600;
    }

    private async postWithRetry<T>(url: string, data: unknown, maxRetries = 3): Promise<AxiosResponse<T>> {
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                return await this.axiosClient.post<T>(url, data);
            } catch (error: unknown) {
                const isRetryable = axios.isAxiosError(error) && this.isRetryableError(error);
                if (!isRetryable || attempt === maxRetries) {
                    throw error;
                }

                const baseDelay = Math.pow(2, attempt) * 1000;
                const jitter = Math.random() * baseDelay * 0.5;
                const delay = baseDelay + jitter;

                this.logger.warn(
                    `Retrying POST ${url} (attempt ${attempt + 1}/${maxRetries}) after ${Math.round(delay)}ms`,
                );

                await new Promise((resolve) => setTimeout(resolve, delay));
            }
        }

        throw new Error("postWithRetry: unexpected end of retry loop");
    }

    /**
     * Creates a configured axios client with authentication and default headers
     */
    private createAxiosClient(): AxiosInstance {
        const client = axios.create({
            headers: {
                "Content-Type": "application/json",
            },
        });

        // Add auth token to every request
        client.interceptors.request.use((config) => {
            const token = this.fluxAuthClaimsService.getFluxToken();
            if (!token) {
                throw new Error("No authentication token available");
            }
            config.headers.Authorization = `Bearer ${token}`;
            return config;
        });

        return client;
    }
}
